import { useCallback, useEffect, useRef } from "react";

interface UseFaviconBadgeProps {
  /**
   * Сбросить счетчик при фокусе на вкладку
   * @default true
   */
  resetOnFocus?: boolean;
}

/**
 * Хук для отображения счетчика непрочитанных уведомлений на фавиконке вкладки.
 * При сворачивании вкладки или переключении на другую - показывает цифру.
 * При возврате на вкладку - сбрасывает счетчик.
 */
export const useFaviconBadge = ({
  resetOnFocus = true,
}: UseFaviconBadgeProps = {}) => {
  const countRef = useRef(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const originalFaviconRef = useRef<string>("");
  const blinkIntervalRef = useRef<number | null>(null);
  const isBlinkingRef = useRef(false);

  // Интервал мигания в миллисекундах
  const BLINK_INTERVAL = 375;

  /**
   * Обновить ссылку на фавиконку в DOM
   */
  const updateFaviconLink = useCallback((dataUrl: string) => {
    let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");

    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }

    link.href = dataUrl;
  }, []);

  /**
   * Восстановить оригинальную фавиконку
   */
  const restoreFavicon = useCallback(() => {
    if (originalFaviconRef.current) {
      updateFaviconLink(originalFaviconRef.current);
    }
  }, [updateFaviconLink]);

  /**
   * Обновить фавиконку с цифрой
   */
  const updateFavicon = useCallback(
    (count: number) => {
      if (count <= 0) {
        restoreFavicon();
        return;
      }

      try {
        const canvas = document.createElement("canvas");
        canvas.width = 32;
        canvas.height = 32;
        canvasRef.current = canvas;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Получаем текущую фавиконку или используем дефолтную
        const faviconLink =
          document.querySelector<HTMLLinkElement>("link[rel*='icon']");
        const faviconUrl = faviconLink?.href || "/favicon.ico";

        // Сохраняем оригинальную фавиконку при первом вызове
        if (!originalFaviconRef.current) {
          originalFaviconRef.current = faviconUrl;
        }

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          // Рисуем оригинальную фавиконку
          ctx.drawImage(img, 0, 0, 32, 32);

          // Рисуем красный круг с цифрой
          const badgeRadius = 15;
          const badgeX = 16;
          const badgeY = 16;

          // Фон бейджа (красный круг)
          ctx.beginPath();
          ctx.arc(badgeX, badgeY, badgeRadius, 0, 2 * Math.PI);
          ctx.fillStyle = "#ff7394";
          ctx.fill();

          // Обводка бейджа (белая)
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 2;
          ctx.stroke();

          // Цифра на бейдже
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 20px Arial";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          // Если число > 99, показываем "99+"
          const displayText = count > 9 ? "9+" : String(count);
          ctx.fillText(displayText, badgeX, badgeY + 1);

          // Обновляем фавиконку
          const dataUrl = canvas.toDataURL("image/png");
          updateFaviconLink(dataUrl);
        };

        // img.onerror = () => {
        //   // Если не удалось загрузить фавиконку, создаем простую с нуля
        //   ctx.fillStyle = "#ff0000";
        //   ctx.fillRect(0, 0, 32, 32);

        //   ctx.fillStyle = "#ffffff";
        //   ctx.font = "bold 20px Arial";
        //   ctx.textAlign = "center";
        //   ctx.textBaseline = "middle";

        //   const displayText = count > 9 ? "9+" : String(count);
        //   ctx.fillText(displayText, 16, 16);

        //   const dataUrl = canvas.toDataURL("image/png");
        //   updateFaviconLink(dataUrl);
        // };

        img.src = faviconUrl;
      } catch (error) {
        console.error("Error updating favicon badge:", error);
      }
    },
    [restoreFavicon, updateFaviconLink]
  );

  /**
   * Остановить мигание
   */
  const stopBlinking = useCallback(() => {
    if (blinkIntervalRef.current !== null) {
      clearInterval(blinkIntervalRef.current);
      blinkIntervalRef.current = null;
      isBlinkingRef.current = false;
      // При остановке показываем бейдж
      if (countRef.current > 0) {
        updateFavicon(countRef.current);
      }
    }
  }, [updateFavicon]);

  /**
   * Запустить мигание фавиконки
   */
  const startBlinking = useCallback(() => {
    if (isBlinkingRef.current) return; // Уже мигает

    isBlinkingRef.current = true;
    let showBadge = true;

    blinkIntervalRef.current = window.setInterval(() => {
      if (showBadge) {
        // Показываем оригинальную фавиконку (пропадание бейджа)
        restoreFavicon();
      } else {
        // Показываем бейдж
        if (countRef.current > 0) {
          updateFavicon(countRef.current);
        }
      }
      showBadge = !showBadge;
    }, BLINK_INTERVAL);
  }, [restoreFavicon, updateFavicon]);

  /**
   * Увеличить счетчик на 1
   */
  const increment = useCallback(() => {
    countRef.current += 1;
    updateFavicon(countRef.current);
    // Запускаем мигание при новом уведомлении
    startBlinking();
  }, [updateFavicon, startBlinking]);

  /**
   * Установить конкретное значение счетчика
   */
  const setCount = useCallback(
    (value: number) => {
      countRef.current = Math.max(0, value);
      updateFavicon(countRef.current);
    },
    [updateFavicon]
  );

  /**
   * Сбросить счетчик и убрать бейдж с фавиконки
   */
  const reset = useCallback(() => {
    countRef.current = 0;
    stopBlinking();
    restoreFavicon();
  }, [stopBlinking, restoreFavicon]);

  /**
   * Получить текущее значение счетчика
   */
  const getCount = useCallback(() => countRef.current, []);

  /**
   * Обработчик видимости вкладки
   */
  const handleVisibilityChange = useCallback(() => {
    if (!document.hidden && resetOnFocus) {
      reset();
    }
  }, [reset, resetOnFocus]);

  /**
   * Обработчик фокуса на окно
   */
  const handleFocus = useCallback(() => {
    if (resetOnFocus) {
      reset();
    }
  }, [reset, resetOnFocus]);

  useEffect(() => {
    // Сохраняем оригинальную фавиконку при монтировании
    const faviconLink =
      document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    if (faviconLink) {
      originalFaviconRef.current = faviconLink.href;
    }

    // Подписываемся на события видимости и фокуса
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      // Очищаем интервал мигания при unmount
      stopBlinking();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [handleVisibilityChange, handleFocus, stopBlinking]);

  return {
    increment,
    setCount,
    reset,
    getCount,
    startBlinking,
    stopBlinking,
  };
};
